import React, { forwardRef } from 'react';
import { StackLayout } from '#components/stack-layout/stack-layout.tsx';
import { GDSHEXColor } from '#theme/theme-types.ts';
import { GDSCustomizableComponent, GDSBackgroundColor } from '#src/types.ts';
import { ComposableCardDismissButton } from '#components/composable-card/composable-card-dismiss-button.tsx';
import { Box } from '#components/box/box.tsx';
import { GDSColor, styled } from '../../theme';
import { ComposableCardMedia } from './composable-card-media';

const DISPLAY_NAME = 'ComposableCardRoot';

export type GDSComposableCardBackgroundColor =
  | Exclude<GDSBackgroundColor, 'tertiary'>
  | GDSHEXColor
  | GDSColor;

export interface GDSComposableCardRootProps extends GDSCustomizableComponent {
  backgroundColor?: GDSComposableCardBackgroundColor;
  onDismiss?: (event: React.MouseEvent) => void;
  dismissButtonLabel?: string;
  children?: React.ReactNode;
  mediaSrc?: string;
  mediaAltText?: string;
  mediaHeight?: string;
  mediaLoading?: 'lazy' | 'eager';
  onCardMouseUp?: (event: React.SyntheticEvent) => void;
  onCardMouseDown?: (event: React.SyntheticEvent) => void;
  backgroundImage?: string;
  backgroundOverlay?: boolean;
}

const StyledCardRoot = styled(StackLayout, {
  border: '$borderWidths$card solid $onSurfaceBorderCard',
  boxShadow: '$card',
  borderRadius: '$card',
  position: 'relative',
  transition: 'border-color $defaultTime ease-in-out',
  overflow: 'hidden',
  height: 'auto',
  '&:has(.GDS-composable-card-action)': {
    '&:hover': {
      cursor: 'pointer',
      border: '$borderWidths$card solid $colors$interactiveFocusOuter',
      'a:hover, button:hover': {
        textDecoration: 'none',
      },
    },
  },
  variants: {
    backgroundColor: {
      primary: {
        backgroundColor: '$surfaceCardPrimary',
      },
      secondary: {
        backgroundColor: '$surfaceCardSecondary',
      },
    },
  },
});

const overlayStyles = {
  content: '',
  position: 'absolute',
  top: 0,
  insetInlineEnd: 0,
  bottom: 0,
  insetInlineStart: 0,
  width: '100%',
  height: '100%',
  opacity: '40%',
};

const getOverlayColor = (backgroundColor: GDSComposableCardBackgroundColor) => {
  if (backgroundColor === 'primary') {
    return '$surfaceCardPrimary';
  }
  if (backgroundColor === 'secondary') {
    return '$surfaceCardSecondary';
  }
  return backgroundColor;
};

export const ComposableCardRoot = forwardRef<
  HTMLDivElement,
  GDSComposableCardRootProps
>(
  (
    {
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      backgroundColor = 'secondary',
      children,
      className,
      css,
      dismissButtonLabel = 'Dismiss',
      mediaAltText,
      mediaHeight,
      mediaSrc,
      mediaLoading = 'lazy',
      onCardMouseDown,
      onCardMouseUp,
      onDismiss,
      backgroundImage,
      backgroundOverlay,
    },
    ref,
  ) => {
    const hasMedia = !!mediaSrc;
    const hasChildren = !!children;
    const shouldRenderFloatingDismissButton = onDismiss && mediaSrc;
    const shouldRenderInlineDismissButton =
      onDismiss && !mediaSrc && hasChildren;

    const isValidVariant =
      backgroundColor === 'primary' || backgroundColor === 'secondary';
    const customBackgroundColor = !isValidVariant ? backgroundColor : undefined;

    const cardBodyStyles = {
      height: '100%',
      backgroundImage: `url(${backgroundImage})`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      ...(backgroundOverlay && {
        position: 'relative',
        '&:before': {
          backgroundColor: getOverlayColor(backgroundColor),
          ...overlayStyles,
        },
      }),
    };

    return (
      <StyledCardRoot
        backgroundColor={isValidVariant ? backgroundColor : undefined}
        className={['GDS-composable-card', className].join(' ')}
        css={{
          ...(customBackgroundColor && {
            backgroundColor: customBackgroundColor,
          }),
          ...(onCardMouseUp && { cursor: 'pointer' }),
          ...css,
        }}
        data-testid={testId}
        onMouseDown={onCardMouseDown}
        onMouseUp={onCardMouseUp}
        ref={ref}
        horizontalAlignment="stretch"
      >
        {shouldRenderFloatingDismissButton && (
          <ComposableCardDismissButton
            onDismiss={onDismiss}
            dismissButtonLabel={dismissButtonLabel}
            isFloatingButton
          />
        )}
        {hasMedia && (
          <ComposableCardMedia
            mediaSrc={mediaSrc}
            mediaAltText={mediaAltText}
            mediaHeight={mediaHeight}
            mediaLoading={mediaLoading}
          />
        )}
        {hasChildren && (
          <Box className="GDS-composable-card-body" css={cardBodyStyles}>
            <StackLayout
              spacing="$half"
              css={{ padding: '$one', position: 'relative' }}
              horizontalAlignment="stretch"
            >
              {shouldRenderInlineDismissButton && (
                <ComposableCardDismissButton
                  onDismiss={onDismiss}
                  dismissButtonLabel={dismissButtonLabel}
                />
              )}
              <StackLayout
                spacing="$one"
                horizontalAlignment="stretch"
                verticalAlignment="top"
              >
                {children}
              </StackLayout>
            </StackLayout>
          </Box>
        )}
      </StyledCardRoot>
    );
  },
);

ComposableCardRoot.displayName = DISPLAY_NAME;
